import React, { useState } from 'react';
import { Table, Button, Modal, Space, Tabs } from 'antd';
import AceEditor from 'react-ace';
import { useTheme } from '@/components/common/ThemeProvider';
import {
    showErrorNotification,
    showNotification,
    showSuccessNotification
} from '@/components/common/utils/notify';

import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import {
    useFspPaymentProcesses,
    useUpdateFspPaymentProcess,
    useDeleteFspPaymentProcess,
    useImportFspPaymentProcesses
} from "@/modules/admin/fsps/hooks/payment-process";
import {DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined} from "@ant-design/icons";
import { FspPaymentProcessImportDTO } from '@/shared/api/model';

interface ProcessFormData {
    fspCode: string;
    languageCode: string;
    name: string;
    description: string;
    processDefinition: string;
}

interface ImportProcessesData {
    fspCode: string;
    processes: Array<{
        languageCode: string;
        name: string;
        description: string;
        version: string;
        steps: Array<{
            order: number;
            instruction: string;
            description?: string;
        }>;
        additionalInfo?: {
            requiredFields: string[];
            supportContact: string;
            notes?: string;
        };
    }>;
}

const PaymentProcessManagement: React.FC = () => {
    const { dark } = useTheme();
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [importJson, setImportJson] = useState('');
    const [selectedProcess, setSelectedProcess] = useState<ProcessFormData | null>(null);
    const { data: processes, isLoading, refetch } = useFspPaymentProcesses();
    const updateProcess = useUpdateFspPaymentProcess();
    const deleteProcess = useDeleteFspPaymentProcess();
    const importProcesses = useImportFspPaymentProcesses();

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const content = e.target?.result as string;
                    // Format JSON for display
                    const formatted = JSON.stringify(JSON.parse(content), null, 2);
                    setImportJson(formatted);
                } catch (error) {
                    showErrorNotification('Invalid JSON', 'Please upload a valid JSON file', dark);
                }
            };
            reader.readAsText(file);
        }
    };

    const handleImport = async () => {
        try {
            const importData: ImportProcessesData = JSON.parse(importJson);
            await importProcesses.mutateAsync(importData as FspPaymentProcessImportDTO);
            showSuccessNotification('Import Successful', 'Payment processes imported successfully', dark);
            setImportModalVisible(false);
            setImportJson('');
            refetch();
        } catch (error) {
            showErrorNotification('Import Failed', 'Failed to import payment processes', dark);
        }
    };

    const handleEdit = (record: ProcessFormData) => {
        setSelectedProcess(record);
        setEditModalVisible(true);
    };

    const handleSave = async () => {
        if (!selectedProcess) return;

        try {
            // Validate JSON
            JSON.parse(selectedProcess.processDefinition);

            await updateProcess.mutateAsync({
                fspCode: selectedProcess.fspCode,
                languageCode: selectedProcess.languageCode,
                name: selectedProcess.name,
                description: selectedProcess.description,
                processDefinition: selectedProcess.processDefinition
            });

            showSuccessNotification('Update Successful', 'Payment process updated successfully', dark);
            setEditModalVisible(false);
            setSelectedProcess(null);
            refetch();
        } catch (error) {
            showErrorNotification('Update Failed', 'Invalid JSON or failed to update process', dark);
        }
    };

    const handleDelete = async (record: ProcessFormData) => {
        try {
            await deleteProcess.mutateAsync({
                fspCode: record.fspCode,
                languageCode: record.languageCode
            });
            showSuccessNotification('Delete Successful', 'Payment process deleted successfully', dark);
            refetch();
        } catch (error) {
            showErrorNotification('Delete Failed', 'Failed to delete payment process', dark);
        }
    };

    const columns = [
        {
            title: 'FSP',
            dataIndex: 'fspCode',
            key: 'fspCode',
        },
        {
            title: 'Language',
            dataIndex: 'languageCode',
            key: 'languageCode',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Version',
            dataIndex: 'version',
            key: 'version',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: ProcessFormData) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleDelete(record)}
                    />
                </Space>
            ),
        },
    ];

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h1 className={`text-2xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>
                    Payment Process Management
                </h1>
                <Space>
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setImportModalVisible(true)}
                    >
                        Import Processes
                    </Button>
                    <Button
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setSelectedProcess({
                                fspCode: '',
                                languageCode: '',
                                name: '',
                                description: '',
                                processDefinition: JSON.stringify({ steps: [] }, null, 2)
                            });
                            setEditModalVisible(true);
                        }}
                    >
                        Add Process
                    </Button>
                </Space>
            </div>

            <Table
                columns={columns}
                // @ts-ignore
                dataSource={processes}
                loading={isLoading}
                rowKey={(record) => `${record.fspCode}-${record.languageCode}`}
            />

            {/* Import Modal */}
            <Modal
                title="Import Payment Processes"
                open={importModalVisible}
                onOk={handleImport}
                onCancel={() => {
                    setImportModalVisible(false);
                    setImportJson('');
                }}
                width={800}
            >
                <div className="space-y-4">
                    <input
                        type="file"
                        accept=".json"
                        onChange={handleFileUpload}
                        className="mb-4"
                    />
                    <AceEditor
                        mode="json"
                        theme={dark ? 'monokai' : 'github'}
                        value={importJson}
                        onChange={setImportJson}
                        name="import-editor"
                        editorProps={{ $blockScrolling: true }}
                        setOptions={{
                            useWorker: false,
                            showPrintMargin: false,
                        }}
                        width="100%"
                        height="400px"
                    />
                </div>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title={selectedProcess?.fspCode ? "Edit Payment Process" : "Add Payment Process"}
                open={editModalVisible}
                onOk={handleSave}
                onCancel={() => {
                    setEditModalVisible(false);
                    setSelectedProcess(null);
                }}
                width={800}
            >
                {selectedProcess && (
                    <div className="space-y-4">
                        <Tabs>
                            <Tabs.TabPane tab="Basic Info" key="basic">
                                <div className="space-y-4">
                                    <div>
                                        <label className={`block mb-2 ${dark ? 'text-white' : 'text-gray-700'}`}>
                                            FSP Code
                                        </label>
                                        <input
                                            type="text"
                                            value={selectedProcess.fspCode}
                                            onChange={(e) => setSelectedProcess({
                                                ...selectedProcess,
                                                fspCode: e.target.value
                                            })}
                                            className="w-full p-2 border rounded"
                                        />
                                    </div>
                                    <div>
                                        <label className={`block mb-2 ${dark ? 'text-white' : 'text-gray-700'}`}>
                                            Language Code
                                        </label>
                                        <input
                                            type="text"
                                            value={selectedProcess.languageCode}
                                            onChange={(e) => setSelectedProcess({
                                                ...selectedProcess,
                                                languageCode: e.target.value
                                            })}
                                            className="w-full p-2 border rounded"
                                        />
                                    </div>
                                    <div>
                                        <label className={`block mb-2 ${dark ? 'text-white' : 'text-gray-700'}`}>
                                            Name
                                        </label>
                                        <input
                                            type="text"
                                            value={selectedProcess.name}
                                            onChange={(e) => setSelectedProcess({
                                                ...selectedProcess,
                                                name: e.target.value
                                            })}
                                            className="w-full p-2 border rounded"
                                        />
                                    </div>
                                    <div>
                                        <label className={`block mb-2 ${dark ? 'text-white' : 'text-gray-700'}`}>
                                            Description
                                        </label>
                                        <textarea
                                            value={selectedProcess.description}
                                            onChange={(e) => setSelectedProcess({
                                                ...selectedProcess,
                                                description: e.target.value
                                            })}
                                            className="w-full p-2 border rounded"
                                            rows={3}
                                        />
                                    </div>
                                </div>
                            </Tabs.TabPane>
                            <Tabs.TabPane tab="Process Definition" key="process">
                                <AceEditor
                                    mode="json"
                                    theme={dark ? 'monokai' : 'github'}
                                    value={selectedProcess.processDefinition}
                                    onChange={(value) => setSelectedProcess({
                                        ...selectedProcess,
                                        processDefinition: value
                                    })}
                                    name="process-editor"
                                    editorProps={{ $blockScrolling: true }}
                                    setOptions={{
                                        useWorker: false,
                                        showPrintMargin: false,
                                    }}
                                    width="100%"
                                    height="400px"
                                />
                            </Tabs.TabPane>
                        </Tabs>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PaymentProcessManagement;